import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { MargensItemAbaixoLinha } from '@triade/contracts';
import { useMargensItensAbaixo } from '@/api/margens';
import { ProfitBadge } from '@/components/profit-badge';
import { formatarMoeda, formatarQuantidade } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Bloco } from './bloco';
import { Paginacao } from './paginacao';
import { TabelaOrdenavel, type ColunaMargem, type Direcao } from './tabela-ordenavel';
import { paraConsulta, ROTULO_TIPO, type BuscaMargens } from './tipos';

type Nivel = 'meta' | 'minimo';
type Campo = 'lucro' | 'receita' | 'codigo' | 'orcamento';

/** Todos os itens negociados abaixo da meta (ou so do minimo), ordenaveis; cada linha abre o orcamento de origem. */
export function SecaoItensAbaixo({ busca, limit = 10 }: { busca: BuscaMargens; limit?: number }) {
  const [nivel, setNivel] = useState<Nivel>('meta');
  const [ordenar, setOrdenar] = useState<Campo>('lucro');
  const [direcao, setDirecao] = useState<Direcao>('asc');
  const [offset, setOffset] = useState(0);
  const consulta = useMargensItensAbaixo({ ...paraConsulta(busca), nivel, ordenar, direcao, limit, offset });
  const dados = consulta.data;

  const ordenarPor = (campo: string) => {
    setOffset(0);
    if (campo === ordenar) setDirecao(direcao === 'asc' ? 'desc' : 'asc');
    else {
      setOrdenar(campo as Campo);
      setDirecao(campo === 'receita' ? 'desc' : 'asc');
    }
  };

  const colunas: ColunaMargem<MargensItemAbaixoLinha>[] = [
    {
      id: 'codigo',
      rotulo: 'Item',
      campo: 'codigo',
      celula: (i) => (
        <span>
          <span className="tabular font-medium">{i.codigo}</span> · {i.descricao}
          <span className="ml-1.5 text-caption text-ink-muted">{ROTULO_TIPO[i.tipo]}</span>
        </span>
      ),
    },
    {
      id: 'orcamento',
      rotulo: 'Orçamento',
      campo: 'orcamento',
      mono: true,
      celula: (i) => (
        <Link to="/vender/orcamentos/$id" params={{ id: i.orcamentoId }} data-testid="abrir-orcamento-item" className="font-semibold text-ink no-underline hover:underline">
          {i.orcamentoNumero}
        </Link>
      ),
    },
    { id: 'cliente', rotulo: 'Cliente · tabela', celula: (i) => <span className="text-ink-secondary">{i.clienteNome ?? 'Sem cliente'} · {i.tabelaNome}</span> },
    { id: 'quantidade', rotulo: 'Qtd.', direita: true, mono: true, celula: (i) => formatarQuantidade(i.exibicao.quantidade) },
    { id: 'receita', rotulo: 'Total', campo: 'receita', direita: true, mono: true, celula: (i) => formatarMoeda(i.exibicao.receita) },
    {
      id: 'lucro',
      rotulo: 'Lucro',
      campo: 'lucro',
      direita: true,
      celula: (i) => <ProfitBadge lucro={i.exibicao.lucro} faixa={i.faixa} meta={i.exibicao.metaLucro} minimo={i.exibicao.minimoLucro} compacto />,
    },
  ];

  const botao = (n: Nivel, rotulo: string) => (
    <button
      type="button"
      aria-pressed={nivel === n}
      onClick={() => {
        setNivel(n);
        setOffset(0);
      }}
      className={cn('h-[34px] rounded-[9px] border bg-card px-3 text-small hover:bg-line-soft', nivel === n ? 'border-primary font-semibold text-primary' : 'border-border')}
    >
      {rotulo}
    </button>
  );

  return (
    <section id="itens-abaixo" aria-label="Itens abaixo da meta" aria-busy={consulta.isFetching} className="rounded-2xl border border-border bg-card px-5 py-[18px]" data-testid="secao-itens-abaixo">
      <div className="mb-3.5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="m-0 text-title font-semibold">Itens negociados abaixo da meta</h2>
          <div className="text-small text-ink-muted">Cada linha é um item de orçamento; o número abre o orçamento.</div>
        </div>
        <div className="flex gap-2">
          {botao('meta', 'Abaixo da meta')}
          {botao('minimo', 'Abaixo do mínimo')}
        </div>
      </div>
      <Bloco
        carregando={consulta.isFetching}
        erro={consulta.error}
        temDados={!!dados}
        vazio={dados?.total === 0}
        mensagemVazia={nivel === 'meta' ? 'Nenhum item negociado abaixo da meta com esses filtros.' : 'Nenhum item abaixo do mínimo com esses filtros.'}
        textoCarregando="Carregando itens…"
        onTentar={() => void consulta.refetch()}
      >
        {dados && (
          <div className="flex flex-col gap-3">
            <TabelaOrdenavel
              legenda="Itens abaixo da meta"
              linhas={dados.itens}
              colunas={colunas}
              ordenar={ordenar}
              direcao={direcao}
              onOrdenar={ordenarPor}
              chave={(i) => `${i.orcamentoId}-${i.seq}`}
              testIdLinha="linha-item-abaixo"
            />
            <Paginacao offset={dados.offset} limit={limit} quantidade={dados.itens.length} total={dados.total} onMudar={setOffset} testId="contagem-itens-abaixo" />
          </div>
        )}
      </Bloco>
    </section>
  );
}
